import { ServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/ServerSideDataGridModel.tsx";
import { AddSalesPartsToSalesContractApi } from "src/api/generated/erp/sales/pricing/api/addSalesPartsToSalesContractApi.ts";
import { SalesContractSalesPartCostContextView } from "src/api/generated/erp/db/types/tables/salesContractSalesPartCostContextView.ts";
import { genericNullableValue } from "src/utils/genericNullableValue.ts";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { moneyColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { AavoSelectField } from "../../../../../common/inputFields/AavoSelectField.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faLink } from "@fortawesome/pro-regular-svg-icons";
import { renderMoneyCellWithRowCurrency } from "src/components/views/erp/sales/salesUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface AddSalesPartsToSalesContractDataGridProps {
	salesContractId: number;
	contractCurrencyCode: string;
	onSubmit: () => void;
}

export const AddSalesPartsToSalesContractDataGrid = ({
	salesContractId,
	contractCurrencyCode,
	onSubmit,
}: AddSalesPartsToSalesContractDataGridProps) => {
	return (
		<ServerSideDataGridModel
			gridId={"85D675DBA2BA01A5"}
			fetchData={(props) =>
				AddSalesPartsToSalesContractApi.getGridData({ salesContractId: salesContractId, ...props })
			}
			getDataModelResult={(data) => data.data}
			getRowId={(row) => row.salesPartId}
			initialParams={{
				searchQuery: "",
				siteId: genericNullableValue<number>(null),
			}}
			render={({ data, dataGridProps, refreshData, selectedRows }) => {
				return (
					<AavoDataGrid<SalesContractSalesPartCostContextView>
						columns={[
							textColumn({
								field: "siteName",
								headerName: i18n.t("site"),
							}),
							textColumn({
								field: "salesPartNo",
								headerName: i18n.t("sales_part_no"),
							}),
							textColumn({
								field: "partDescription_1",
								headerName: i18n.t("description_1"),
								width: 150,
							}),
							textColumn({
								field: "partDescription_2",
								headerName: i18n.t("description_2"),
								width: 150,
							}),
							moneyColumn({
								field: "costInDomesticCurrencyAsPriceUnit",
								headerName: i18n.t("cost_in_domestic_currency"),
								width: 160,
								renderCell: renderMoneyCellWithRowCurrency((row) => row.domesticCurrencyCode ?? ""),
							}),
							moneyColumn({
								field: "costInContractCurrencyAsPriceUnit",
								headerName: i18n.t("cost_in_sales_currency"),
								width: 170,
								renderCell: renderMoneyCellWithRowCurrency(
									(row) => row.contractCurrencyCode ?? contractCurrencyCode,
								),
							}),
							textColumn({
								field: "salesPriceUnit",
								headerName: i18n.t("price_unit"),
							}),
						]}
						actionBarComponents={
							<>
								<AavoTextField
									label={i18n.t("search")}
									onSubmit={async (value) => {
										await refreshData({ searchQuery: value });
									}}
								/>
								<AavoSelectField
									label={i18n.t("site")}
									options={data.siteOptions}
									getOptionLabel={(option) => option.siteName}
									getOptionKey={(option) => option.siteId}
									onChange={async (value) => {
										await refreshData({ siteId: value });
									}}
									minWidth={150}
								/>
								<AsyncButton
									icon={faLink}
									label={i18n.t("connect_selected")}
									variant={"outlined"}
									onClick={async () => {
										await AddSalesPartsToSalesContractApi.addSelectedParts({
											salesContractId: salesContractId,
											salesPartIds: selectedRows.map((row) => row.salesPartId),
										});
										onSubmit();
									}}
								/>
							</>
						}
						agentContext={{
							key: defineAgentUiContextKey("erp.sales.pricing.addSalesPartsToSalesContract.grid"),
							data: {
								salesContractId,
								contractCurrencyCode,
							},
							selectedRowPathSegment: (row) => `${i18n.t("sales_part")} ${row.salesPartNo}`,
							rowFields: ["salesPartId", "partId", "salesPartNo", "siteId"],
						}}
						{...dataGridProps}
					/>
				);
			}}
		/>
	);
};
